/**
 * Multi-tenant SaaS context (EdgeFinances).
 *
 * Primary source of truth is the backend API (Node/Express/MongoDB). The local
 * registry below is kept as an offline cache / fallback so the app still works
 * when the API is unreachable (e.g. demos without a running server).
 */
import {
  authApi,
  billingApi,
  adminApi,
  setAuthToken,
  clearAuthToken,
  ApiError,
  type ApiOrganization,
  type BillingTerm,
} from '../api';
import { setCurrentUser } from './permissions';
import { setSubscriptionCache } from './subscription';
import { applyAuthLedgers } from './ledger';

export const PRODUCT_NAME = 'Edge Finance Pro';
export const PRODUCT_TAGLINE = 'Smart Finance Solutions';

const REGISTRY_KEY = 'edgefin_org_registry_v1';
const CHECKOUT_TENANT_KEY = 'ef_checkout_tenant_id';

export type SubscriptionStatus = 'pending_payment' | 'active' | 'past_due';

export type Organization = {
  id: string;
  name: string;
  slug: string;
  adminEmail: string;
  passwordHash: string;
  subscriptionStatus: SubscriptionStatus;
  planId: string | null;
  billingTerm?: BillingTerm | null;
  subscriptionExpiresAt?: string | null;
  subscriptionSource?: 'trial' | 'paid' | 'demo' | null;
  trialOfferId?: string | null;
  trialRedeemedAt?: string | null;
  createdAt: string;
  /** Last time this org’s tenant workspace was opened on this device (best-effort; replace with server analytics). */
  lastWorkspaceAt?: string;
  /** Platform operator: blocks tenant workspace until released (e.g. non-payment). */
  accountHold?: boolean;
  accountHoldReason?: string;
  accountHeldAt?: string;
};

type Registry = { orgs: Organization[] };

const SESSION = {
  tenantId: 'ef_tenant_id',
  tenantName: 'ef_tenant_name',
  tenantSlug: 'ef_tenant_slug',
  planId: 'ef_plan_id',
  subscriptionStatus: 'ef_subscription_status',
} as const;

export async function sha256Hex(message: string): Promise<string> {
  const msgBuffer = new TextEncoder().encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

function readRegistry(): Registry {
  try {
    const raw = localStorage.getItem(REGISTRY_KEY);
    if (!raw) return { orgs: [] };
    const parsed = JSON.parse(raw) as Registry;
    return Array.isArray(parsed.orgs) ? parsed : { orgs: [] };
  } catch {
    return { orgs: [] };
  }
}

function writeRegistry(registry: Registry): void {
  localStorage.setItem(REGISTRY_KEY, JSON.stringify(registry));
}

/** Map a backend organization onto the local Organization shape (cache). */
function fromApiOrg(api: ApiOrganization, passwordHash = ''): Organization {
  return {
    id: api.id,
    name: api.name,
    slug: api.slug,
    adminEmail: api.adminEmail,
    passwordHash,
    subscriptionStatus: api.subscriptionStatus,
    planId: api.planId,
    billingTerm: (api as { billingTerm?: BillingTerm }).billingTerm || 'monthly',
    subscriptionExpiresAt: api.subscriptionExpiresAt,
    subscriptionSource: api.subscriptionSource,
    trialOfferId: api.trialOfferId,
    trialRedeemedAt: api.trialRedeemedAt,
    createdAt: api.createdAt,
    lastWorkspaceAt: api.lastWorkspaceAt,
    accountHold: api.accountHold,
    accountHoldReason: api.accountHoldReason,
    accountHeldAt: api.accountHeldAt,
  };
}

/** Insert/update a backend org in the local cache registry (keeps existing hash). */
function upsertCachedOrg(api: ApiOrganization): Organization {
  const reg = readRegistry();
  const i = reg.orgs.findIndex((o) => o.id === api.id);
  const prevHash = i >= 0 ? reg.orgs[i].passwordHash : '';
  const merged = fromApiOrg(api, prevHash);
  if (i >= 0) reg.orgs[i] = merged;
  else reg.orgs.push(merged);
  writeRegistry(reg);
  return merged;
}

/** Refresh local org registry + session flags from `/auth/me` (hold, subscription, plan). */
export function cacheOrganizationFromApi(api: ApiOrganization): Organization {
  const org = upsertCachedOrg(api);
  refreshTenantSessionFromRegistry();
  return org;
}

/** True for connection-level failures where falling back to local cache is sensible. */
function isNetworkError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 0;
}

export function listOrganizations(): Organization[] {
  return readRegistry().orgs;
}

export function getOrganizationById(id: string): Organization | undefined {
  return readRegistry().orgs.find((o) => o.id === id);
}

export function getOrganizationByEmail(email: string): Organization | undefined {
  const e = email.trim().toLowerCase();
  return readRegistry().orgs.find((o) => o.adminEmail.toLowerCase() === e);
}

export function slugifyOrgName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48) || 'org';
}

export async function registerOrganization(input: {
  name: string;
  adminEmail: string;
  password: string;
  planId?: string | null;
  trialSecretKey?: string;
}): Promise<Organization> {
  const email = input.adminEmail.trim().toLowerCase();

  try {
    const res = await authApi.register({
      name: input.name.trim(),
      adminEmail: email,
      password: input.password,
      planId: input.planId ?? null,
      trialSecretKey: input.trialSecretKey?.trim() || undefined,
    });
    // Store the JWT so the new admin can immediately complete checkout.
    setAuthToken(res.token);
    if (res.user) setCurrentUser(res.user);
    applyAuthLedgers(res);
    if (res.organization) {
      const org = upsertCachedOrg(res.organization);
      setTenantSession(org);
      localStorage.setItem('isLoggedIn', 'true');
      if (org.subscriptionStatus === 'active') void refreshSubscriptionCache();
      return org;
    }
  } catch (err) {
    if (!isNetworkError(err)) {
      // Surface real backend errors (e.g. email already taken, weak password).
      throw err instanceof Error ? err : new Error('Registration failed.');
    }
    // else: fall through to local-only registration
  }

  // Offline fallback: create the org in the local registry.
  if (getOrganizationByEmail(email)) {
    throw new Error('An organization is already registered with this email.');
  }
  const id = crypto.randomUUID();
  const slug = slugifyOrgName(input.name);
  const passwordHash = await sha256Hex(input.password);
  const org: Organization = {
    id,
    name: input.name.trim(),
    slug,
    adminEmail: email,
    passwordHash,
    subscriptionStatus: 'pending_payment',
    planId: input.planId ?? null,
    createdAt: new Date().toISOString(),
  };
  const reg = readRegistry();
  reg.orgs.push(org);
  writeRegistry(reg);
  return org;
}

export async function refreshSubscriptionCache(): Promise<void> {
  try {
    const res = await billingApi.status();
    setSubscriptionCache(res.subscription);
  } catch {
    /* ignore when not logged in as tenant admin */
  }
}

export function setCheckoutTenantId(tenantId: string): void {
  sessionStorage.setItem(CHECKOUT_TENANT_KEY, tenantId);
}

export function consumeCheckoutTenantId(): string | null {
  const v = sessionStorage.getItem(CHECKOUT_TENANT_KEY);
  if (v) sessionStorage.removeItem(CHECKOUT_TENANT_KEY);
  return v;
}

export function peekCheckoutTenantId(): string | null {
  return sessionStorage.getItem(CHECKOUT_TENANT_KEY);
}

export function activateSubscription(orgId: string, planId: string): void {
  const reg = readRegistry();
  const org = reg.orgs.find((o) => o.id === orgId);
  if (!org) return;
  org.subscriptionStatus = 'active';
  org.planId = planId;
  writeRegistry(reg);
}

/**
 * Complete checkout against the backend (demo payment) and refresh the local
 * cache + session. Falls back to local activation when the API is unreachable.
 */
export async function activateSubscriptionRemote(orgId: string, planId: string): Promise<void> {
  try {
    const res = await billingApi.checkout(planId);
    upsertCachedOrg(res.organization);
    setSubscriptionCache(res.subscription);
    refreshTenantSessionFromRegistry();
    return;
  } catch (err) {
    if (!isNetworkError(err)) {
      throw err instanceof Error ? err : new Error('Could not complete checkout.');
    }
  }
  // Offline fallback.
  activateSubscription(orgId, planId);
  refreshTenantSessionFromRegistry();
}

export function setTenantSession(org: Pick<Organization, 'id' | 'name' | 'slug' | 'planId' | 'subscriptionStatus'>): void {
  localStorage.setItem(SESSION.tenantId, org.id);
  localStorage.setItem(SESSION.tenantName, org.name);
  localStorage.setItem(SESSION.tenantSlug, org.slug);
  localStorage.setItem(SESSION.planId, org.planId ?? '');
  localStorage.setItem(SESSION.subscriptionStatus, org.subscriptionStatus);
}

export function getTenantId(): string | null {
  return localStorage.getItem(SESSION.tenantId);
}

export function getTenantDisplayName(): string | null {
  return localStorage.getItem(SESSION.tenantName);
}

/** Call after `activateSubscription` so an already-signed-in session reflects `active`. */
export function refreshTenantSessionFromRegistry(): void {
  const id = getTenantId();
  if (!id) return;
  const org = getOrganizationById(id);
  if (org) setTenantSession(org);
}

export function clearTenantSession(): void {
  Object.values(SESSION).forEach((k) => localStorage.removeItem(k));
}

export function clearAuthAndTenant(): void {
  try {
    sessionStorage.removeItem('ef_super_saved');
    sessionStorage.removeItem('ef_impersonating');
  } catch {
    /* ignore */
  }
  clearTenantSession();
  clearAuthToken();
  setCurrentUser(null);
  localStorage.removeItem('token');
  localStorage.removeItem('userId');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('role');
  localStorage.removeItem('isLoggedIn');
}

export async function trySaasLogin(email: string, password: string): Promise<Organization | null> {
  try {
    const res = await authApi.login({ email: email.trim().toLowerCase(), password });
    if (res.user.role !== 'ADMIN' || !res.organization) {
      // Not a tenant admin (e.g. super admin) — let other handlers deal with it.
      return null;
    }
    setAuthToken(res.token);
    setCurrentUser(res.user);
    return upsertCachedOrg(res.organization);
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 400)) {
      return null; // invalid credentials — backend is the authority
    }
    if (!isNetworkError(err)) {
      return null;
    }
    // Offline fallback: verify against the local cache hash.
    const org = getOrganizationByEmail(email);
    if (!org) return null;
    const hash = await sha256Hex(password);
    if (hash !== org.passwordHash) return null;
    return org;
  }
}

/** Backend super-admin sign-in. Returns true and stores the JWT on success. */
export async function trySuperAdminApiLogin(email: string, password: string): Promise<boolean> {
  try {
    const res = await authApi.login({ email: email.trim().toLowerCase(), password });
    if (res.user.role !== 'SUPER_ADMIN') return false;
    setAuthToken(res.token);
    setCurrentUser(res.user);
    return true;
  } catch {
    return false;
  }
}

export type LoginOutcome =
  | { status: 'super' }
  | { status: 'admin'; org: Organization }
  | { status: 'invalid'; message?: string }
  | { status: 'noaccount' }
  | { status: 'offline' };

/**
 * Google Sign-In (login only). Verifies the Google credential against the
 * backend and signs the matching account in. Returns `noaccount` when the
 * Google email has no organization yet so the UI can prompt registration.
 */
export async function attemptGoogleLogin(credential: string): Promise<LoginOutcome> {
  try {
    const res = await authApi.google({ credential });
    setAuthToken(res.token);
    setCurrentUser(res.user);
    if (res.user.role === 'SUPER_ADMIN') return { status: 'super' };
    if (res.organization) {
      void refreshSubscriptionCache();
      return { status: 'admin', org: upsertCachedOrg(res.organization) };
    }
    return { status: 'invalid' };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return { status: 'noaccount' };
    if (err instanceof ApiError && (err.status === 401 || err.status === 400)) {
      return { status: 'invalid', message: err.message };
    }
    return { status: 'offline' };
  }
}

/**
 * Register a new organization using Google Sign-In. If the Google email already
 * has an account the backend signs it in instead (returning its organization).
 */
export async function registerOrganizationWithGoogle(input: {
  credential: string;
  name: string;
  planId?: string | null;
  trialSecretKey?: string;
}): Promise<Organization> {
  const res = await authApi.google({
    credential: input.credential,
    name: input.name.trim(),
    planId: input.planId ?? null,
    trialSecretKey: input.trialSecretKey?.trim() || undefined,
  });
  setAuthToken(res.token);
  setCurrentUser(res.user);
  if (res.organization) {
    const org = upsertCachedOrg(res.organization);
    setTenantSession(org);
    localStorage.setItem('isLoggedIn', 'true');
    if (org.subscriptionStatus === 'active') void refreshSubscriptionCache();
    return org;
  }
  throw new Error('Could not create your organization with Google.');
}

/**
 * Single entry point for sign-in against the backend.
 * Distinguishes bad credentials (backend reachable) from an unreachable backend
 * so the UI only falls back to offline mode when the server truly can't be reached.
 */
export async function attemptLogin(email: string, password: string): Promise<LoginOutcome> {
  try {
    const res = await authApi.login({ email: email.trim().toLowerCase(), password });
    setAuthToken(res.token);
    setCurrentUser(res.user);
    applyAuthLedgers(res as { ledgers?: unknown[]; defaultLedgerId?: string; activeLedgerId?: string });
    if (res.user.role === 'SUPER_ADMIN') return { status: 'super' };
    if (res.user.role === 'LEDGER' || res.organization) {
      void refreshSubscriptionCache();
      return { status: 'admin', org: upsertCachedOrg(res.organization) };
    }
    return { status: 'invalid' };
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 400)) {
      return { status: 'invalid' };
    }
    // Network / CORS / server down → allow offline fallback.
    return { status: 'offline' };
  }
}

export function setSyntheticSessionForApiUser(email: string, userId: string): void {
  const org = getOrganizationByEmail(email);
  if (org && org.subscriptionStatus === 'active') {
    setTenantSession(org);
    return;
  }
  const shadowId = `ws_${userId}`;
  setTenantSession({
    id: shadowId,
    name: 'Workspace',
    slug: 'workspace',
    planId: 'api',
    subscriptionStatus: 'active',
  });
}

export function isSubscriptionActiveForDashboard(): boolean {
  const token = localStorage.getItem('token');
  if (!token) return false;
  if (token.startsWith('super-frontend-token')) return false;
  if (token.startsWith('static-frontend-token')) return true;
  if (token.startsWith('local-frontend-token')) return true;
  const status = localStorage.getItem(SESSION.subscriptionStatus);
  return status === 'active';
}

export function needsCheckoutResume(): boolean {
  const token = localStorage.getItem('token');
  const logged = localStorage.getItem('isLoggedIn') === 'true';
  if (!logged || !token) return false;
  if (token.startsWith('super-frontend-token')) return false;
  if (token.startsWith('static-frontend-token')) return false;
  if (token.startsWith('local-frontend-token')) return false;
  if (isCurrentTenantOnHold()) return false;
  const status = localStorage.getItem(SESSION.subscriptionStatus);
  return status !== 'active';
}

/** True when the signed-in tenant id matches a registry org with a platform payment hold. */
export function isCurrentTenantOnHold(): boolean {
  const tid = getTenantId();
  if (!tid) return false;
  return Boolean(getOrganizationById(tid)?.accountHold);
}

/**
 * Platform operator only: suspend or restore tenant workspace access.
 * Does not change plan or subscription status — use billing flows for that.
 */
export function setOrganizationAccountHold(orgId: string, held: boolean, reason?: string): void {
  const reg = readRegistry();
  const org = reg.orgs.find((o) => o.id === orgId);
  if (!org) return;
  org.accountHold = held;
  if (held) {
    const r = (reason ?? '').trim() || 'Payment or compliance hold';
    org.accountHoldReason = r;
    org.accountHeldAt = new Date().toISOString();
  } else {
    delete org.accountHoldReason;
    delete org.accountHeldAt;
    org.accountHold = false;
  }
  writeRegistry(reg);
}

export type OrganizationPublic = Omit<Organization, 'passwordHash'> & {
  dataKeys?: number;
  whatsappMessagesInPeriod?: number;
};

export function listOrganizationsPublic(): OrganizationPublic[] {
  return readRegistry().orgs.map((o) => {
    const { passwordHash: _omit, ...pub } = o;
    return pub;
  });
}

/**
 * Super-admin console data source. Fetches every organization from the backend
 * and refreshes the local cache. Falls back to the local registry when offline.
 */
export async function fetchOrganizationsForAdmin(params?: {
  from?: string;
  to?: string;
}): Promise<OrganizationPublic[]> {
  try {
    const { organizations } = await adminApi.listOrgs(params);
    // Refresh cache so offline reads stay reasonably current.
    organizations.forEach((o) => upsertCachedOrg(o));
    return organizations.map((o) => {
      const { passwordHash: _omit, ...pub } = fromApiOrg(o);
      return { ...pub, dataKeys: o.dataKeys, whatsappMessagesInPeriod: o.whatsappMessagesInPeriod };
    });
  } catch (err) {
    // Network or auth hiccup: fall back to the local cache instead of blanking the console.
    const status = err instanceof ApiError ? err.status : -1;
    if (status === 0 || status === 401 || status === 403) {
      return listOrganizationsPublic();
    }
    throw err instanceof Error ? err : new Error('Failed to load organizations.');
  }
}

/** Backend hold/release with local-cache fallback. */
export async function setOrganizationAccountHoldRemote(
  orgId: string,
  held: boolean,
  reason?: string
): Promise<void> {
  try {
    const res = await adminApi.setHold(orgId, held, reason);
    upsertCachedOrg(res.organization);
    return;
  } catch (err) {
    if (!isNetworkError(err)) throw err instanceof Error ? err : new Error('Failed to update hold.');
  }
  setOrganizationAccountHold(orgId, held, reason);
}

export function updateOrganization(
  orgId: string,
  patch: Partial<Pick<Organization, 'subscriptionStatus' | 'planId' | 'name' | 'slug'>>
): void {
  const reg = readRegistry();
  const org = reg.orgs.find((o) => o.id === orgId);
  if (!org) return;
  if (patch.name != null) org.name = patch.name;
  if (patch.slug != null) org.slug = patch.slug;
  if (patch.subscriptionStatus != null) org.subscriptionStatus = patch.subscriptionStatus;
  if (patch.planId !== undefined) org.planId = patch.planId;
  writeRegistry(reg);
}

export function deleteOrganization(orgId: string): void {
  const reg = readRegistry();
  reg.orgs = reg.orgs.filter((o) => o.id !== orgId);
  writeRegistry(reg);
}

/** Record that someone opened this organization’s workspace (tenant admins’ app) on this browser. */
export function touchOrganizationLastActivity(orgId: string): void {
  const reg = readRegistry();
  const org = reg.orgs.find((o) => o.id === orgId);
  if (!org) return;
  org.lastWorkspaceAt = new Date().toISOString();
  writeRegistry(reg);
}

export const SUPER_ADMIN_ROLE = 'SUPER_ADMIN' as const;
